import { useEffect, useState } from "react";
import { GraduationCap, X, ArrowRight, RotateCcw, LogIn, Camera } from "lucide-react";

/**
 * One-shot onboarding modal that appears the FIRST time a user activates
 * Schulungs-Modus. Persists a flag in localStorage per user so it never
 * shows again afterwards. Triggered explicitly via the `open` prop.
 */
const STORAGE_KEY = (userId) => `novatus_schulung_onboarding_seen:${userId || "anon"}`;

export const SchulungOnboardingModal = ({ open, onClose, userId }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) setVisible(true);
  }, [open]);

  if (!visible) return null;

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY(userId), "1"); } catch {/* noop */}
    setVisible(false);
    onClose?.();
  };

  return (
    <div
      data-testid="schulung-onboarding-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={close}
    >
      <div
        className="relative max-w-lg w-full bg-[#18181B] border border-violet-500/40 rounded-2xl shadow-2xl p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="schulung-onboarding-close"
          className="absolute top-3 right-3 h-9 w-9 rounded-lg bg-zinc-800/80 hover:bg-zinc-700 inline-flex items-center justify-center text-zinc-300"
          aria-label="Schließen"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-violet-500/15 border border-violet-500/40 flex items-center justify-center text-violet-300 flex-shrink-0">
            <GraduationCap size={22} />
          </div>
          <div className="min-w-0">
            <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-violet-300 mb-1">Schulungs-Modus aktiv</div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-50 leading-tight">
              So startest Du eine Vor-Ort-Schulung
            </h2>
            <p className="text-zinc-400 text-sm mt-1.5">
              Echte Kundendaten sind während der Schulung systemweit ausgeblendet – DSGVO-konform.
            </p>
          </div>
        </div>

        <ol className="space-y-3 mt-5">
          <li className="flex gap-3 bg-[#09090B] border border-zinc-800 rounded-xl px-3.5 py-3" data-testid="onboarding-step-1">
            <div className="h-8 w-8 rounded-lg bg-violet-500/15 border border-violet-500/40 text-violet-300 font-bold inline-flex items-center justify-center text-sm flex-shrink-0">1</div>
            <div className="min-w-0">
              <div className="font-semibold text-zinc-100 inline-flex items-center gap-2 text-[14px]">
                <RotateCcw size={14} className="text-violet-300" /> Demo-Datensatz wählen &amp; zurücksetzen
              </div>
              <div className="text-[12.5px] text-zinc-400 mt-1">
                <b>Sabine Schulte</b> für den vorderen Teil (Lead → Angebot) oder <b>Hans Maier</b> für den hinteren Teil (Auftrag → Inbetriebnahme). Reset-Buttons stehen unten in dieser Verwaltung bereit.
              </div>
            </div>
          </li>

          <li className="flex gap-3 bg-[#09090B] border border-zinc-800 rounded-xl px-3.5 py-3" data-testid="onboarding-step-2">
            <div className="h-8 w-8 rounded-lg bg-violet-500/15 border border-violet-500/40 text-violet-300 font-bold inline-flex items-center justify-center text-sm flex-shrink-0">2</div>
            <div className="min-w-0">
              <div className="font-semibold text-zinc-100 inline-flex items-center gap-2 text-[14px]">
                <LogIn size={14} className="text-violet-300" /> Trainee anmelden lassen
              </div>
              <div className="text-[12.5px] text-zinc-400 mt-1">
                Der Trainee meldet sich mit seinem Vertrieb-Account an und sieht ausschließlich die Demo-Projekte. Alles in seinem Workflow ist real – nur die Daten sind synthetisch.
              </div>
            </div>
          </li>

          <li className="flex gap-3 bg-[#09090B] border border-zinc-800 rounded-xl px-3.5 py-3" data-testid="onboarding-step-3">
            <div className="h-8 w-8 rounded-lg bg-violet-500/15 border border-violet-500/40 text-violet-300 font-bold inline-flex items-center justify-center text-sm flex-shrink-0">3</div>
            <div className="min-w-0">
              <div className="font-semibold text-zinc-100 inline-flex items-center gap-2 text-[14px]">
                <Camera size={14} className="text-violet-300" /> Workflow durchspielen
              </div>
              <div className="text-[12.5px] text-zinc-400 mt-1">
                Vor-Ort-Aufnahme, Tech-Check, Heizlast, Angebot, Unterschrift, AZ1/AZ2, Übergabe an Installation, Inbetriebnahme-Protokoll – alles wie im Echtbetrieb.
              </div>
            </div>
          </li>
        </ol>

        <div className="mt-6 text-[12px] text-zinc-300 bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
          <b className="text-zinc-300">Wichtig:</b> Die Reset-Buttons sind nur sichtbar, solange der Schulungs-Modus EIN ist. Schaltet ihr den Modus aus, verschwinden die Buttons automatisch – kein versehentlicher Reset im echten Betrieb möglich.
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={close}
            data-testid="schulung-onboarding-confirm"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-violet-500 hover:bg-violet-400 text-white font-bold text-[14px] active:scale-95 transition"
          >
            Verstanden – los geht&apos;s <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

/** Helper: returns true if the user has not seen the onboarding before. */
export const hasSeenSchulungOnboarding = (userId) => {
  try { return localStorage.getItem(STORAGE_KEY(userId)) === "1"; }
  catch { return false; }
};
